import { Controller } from '@hotwired/stimulus';

// PROTOTYPE — Video | Both | Text column balance (video left, transcript right).
export default class extends Controller {
  static targets = [
    'indicator',
    'videoOption',
    'bothOption',
    'textOption',
    'videoCol',
    'transcriptCol',
    'videoInner',
    'transcriptPanel',
    'transcriptScroll',
    'watchRow'
  ];
  static values = {
    mode: { type: String, default: 'video' },
    embed: { type: String, default: 'longform' }
  };

  static MODES = ['video', 'both', 'text'];

  connect() {
    this.onResize = () => this.apply();
    window.addEventListener('resize', this.onResize);
    if (this.hasVideoInnerTarget) {
      this.videoObserver = new ResizeObserver(() => this.syncTranscriptHeight());
      this.videoObserver.observe(this.videoInnerTarget);
    }
    this.apply();
  }

  disconnect() {
    window.removeEventListener('resize', this.onResize);
    this.videoObserver?.disconnect();
  }

  select(event) {
    const mode = event.params.mode;
    if (!this.constructor.MODES.includes(mode)) return;
    this.modeValue = mode;
  }

  modeValueChanged() {
    this.apply();
  }

  apply() {
    const mode = this.modeValue;
    const videoFocus = mode === 'video';
    const bothFocus = mode === 'both';
    const textFocus = mode === 'text';
    const wide = window.matchMedia('(min-width: 768px)').matches;

    if (this.hasVideoColTarget) {
      const textFocusDesktop = wide && textFocus;
      this.videoColTarget.classList.toggle('proto-watch-focus__video-col--text', textFocusDesktop);

      if (!wide) {
        this.videoColTarget.style.flex = '';
      } else if (videoFocus) {
        this.videoColTarget.style.flex = '2 1 0%';
      } else if (bothFocus) {
        this.videoColTarget.style.flex = '1 1 0%';
      } else {
        const narrow = this.embedValue === 'shorts' ? '11rem' : '14rem';
        this.videoColTarget.style.flex = `0 0 ${narrow}`;
      }
    }
    if (this.hasTranscriptColTarget) {
      if (!wide) {
        this.transcriptColTarget.style.flex = '';
      } else if (videoFocus) {
        this.transcriptColTarget.style.flex = '1 1 0%';
      } else if (bothFocus) {
        this.transcriptColTarget.style.flex = '1 1 0%';
      } else {
        this.transcriptColTarget.style.flex = '1 1 0%';
      }
    }

    this.element.classList.toggle('proto-watch-focus--text', wide && textFocus);
    this.element.classList.toggle('proto-watch-focus--both', wide && bothFocus);

    if (this.hasWatchRowTarget) {
      this.watchRowTarget.classList.toggle('proto-watch-focus__row--text', wide && textFocus);
    }

    if (this.hasIndicatorTarget) {
      const index = this.constructor.MODES.indexOf(mode);
      const step = index < 0 ? 0 : index;
      this.indicatorTarget.style.transform = `translateX(${step * 100}%)`;
    }

    this.updateOptionButton(this.videoOptionTarget, mode === 'video');
    if (this.hasBothOptionTarget) {
      this.updateOptionButton(this.bothOptionTarget, mode === 'both');
    }
    this.updateOptionButton(this.textOptionTarget, mode === 'text');

    if (this.hasVideoInnerTarget) {
      this.videoInnerTarget.dataset.focus = mode;
    }
    if (this.hasTranscriptPanelTarget) {
      this.transcriptPanelTarget.dataset.focus = mode;
    }
    if (this.hasTranscriptScrollTarget) {
      this.transcriptScrollTarget.classList.toggle('proto-transcript-panel--text-focus', textFocus && wide);
    }

    requestAnimationFrame(() => this.syncTranscriptHeight());
  }

  updateOptionButton(button, active) {
    if (!button) return;
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.classList.toggle('text-white', active);
    button.classList.toggle('text-slate-700', !active);
  }

  syncTranscriptHeight() {
    if (!this.hasTranscriptScrollTarget || !this.hasVideoInnerTarget) return;

    const wide = window.matchMedia('(min-width: 768px)').matches;
    if (!wide) {
      this.transcriptScrollTarget.style.height = '';
      this.transcriptScrollTarget.style.maxHeight = '';
      this.transcriptScrollTarget.style.minHeight = '';
      return;
    }

    const height = this.videoInnerTarget.offsetHeight;
    if (height <= 0) return;

    const matchVideoHeight = this.modeValue === 'video' || this.modeValue === 'both';
    if (matchVideoHeight) {
      this.transcriptScrollTarget.style.height = `${height}px`;
      this.transcriptScrollTarget.style.maxHeight = `${height}px`;
      this.transcriptScrollTarget.style.minHeight = '';
    } else {
      this.transcriptScrollTarget.style.minHeight = '';
      this.transcriptScrollTarget.style.height = '';
      this.transcriptScrollTarget.style.maxHeight = '';
    }
  }
}
